/**
 * Raíz de la app.
 *
 * Un solo código, cuatro modos. El rol del usuario decide qué pantalla se
 * abre. Si se detecta un token de mesa por QR (`/mesa/:token` o `?mesa=token`),
 * se abre directamente el menú del comensal sin exigir login.
 */
import { useEffect, useRef, useState } from 'react';
import { Login } from './pantallas/Login';
import { Catalogo } from './pantallas/Catalogo';
import { MenuQR } from './pantallas/MenuQR';
import { GestionMesas } from './pantallas/GestionMesas';
import { Cocina } from './pantallas/Cocina';
import { Mesero } from './pantallas/Mesero';
import { Caja } from './pantallas/Caja';
import { Facturacion } from './pantallas/Facturacion';
import { Repartidor } from './pantallas/Repartidor';
import { PosAbarrotes } from './pantallas/PosAbarrotes';
import { Reportes } from './pantallas/Reportes';
import { Sucursales } from './pantallas/Sucursales';
import { Personal } from './pantallas/Personal';
import { Dispositivos } from './pantallas/Dispositivos';
import { sesion, type Usuario } from './lib/sesion';
import { api } from './lib/api';
import { conectar, desconectar, alCambiarConexion, type EstadoConexion } from './lib/socket';

/** Franja de aviso cuando se cae la red. En cocina esto no puede ser sutil. */
function AvisoConexion() {
  const [estado, setEstado] = useState<EstadoConexion>('desconectado');
  useEffect(() => alCambiarConexion(setEstado), []);

  if (estado === 'conectado') return null;

  return (
    <div
      role="status"
      className={`sticky top-0 z-30 px-4 py-2 text-center font-titulo text-sm font-semibold text-white ${
        estado === 'conectando' ? 'bg-miel' : 'bg-barro'
      }`}
    >
      {estado === 'conectando'
        ? 'Reconectando…'
        : 'Sin conexión con el servidor. Revisa el WiFi del local.'}
    </div>
  );
}

/** Barra del dueño. El personal de piso no la ve: su rol tiene una sola vista. */
function BarraAdmin({
  usuario,
  vista,
  alCambiar,
  restaurantes,
  restauranteId,
  alCambiarRestaurante,
}: {
  usuario: Usuario;
  vista: string;
  alCambiar: (v: string) => void;
  restaurantes: Sucursal[];
  restauranteId: number | null;
  alCambiarRestaurante: (id: number) => void;
}) {
  const principales = [
    { id: 'menu', etiqueta: 'Menú' },
    { id: 'caja', etiqueta: 'Caja' },
    { id: 'cocina', etiqueta: 'Cocina' },
    { id: 'mesero', etiqueta: 'Mesero' },
    { id: 'repartidor', etiqueta: 'Repartidor' },
    { id: 'mesas', etiqueta: 'Mesas QR' },
    { id: 'facturacion', etiqueta: 'Facturación' },
    { id: 'reportes', etiqueta: 'Reportes' },
  ];
  const secundarias = [
    { id: 'abarrotes', etiqueta: 'POS Abarrotes' },
    { id: 'sucursales', etiqueta: 'Sucursales' },
    { id: 'personal', etiqueta: 'Personal' },
    { id: 'dispositivos', etiqueta: 'Dispositivos' },
  ];

  const enSecundaria = secundarias.some((v) => v.id === vista);
  const [menuAbierto, setMenuAbierto] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuAbierto) return;
    function alHacerClickFuera(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuAbierto(false);
    }
    document.addEventListener('mousedown', alHacerClickFuera);
    return () => document.removeEventListener('mousedown', alHacerClickFuera);
  }, [menuAbierto]);

  const iniciales = usuario.nombre
    .split(' ')
    .map((p) => p[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  function pestana(v: { id: string; etiqueta: string }) {
    const activa = vista === v.id;
    return (
      <button
        key={v.id}
        onClick={() => alCambiar(v.id)}
        className={`whitespace-nowrap border-b-2 px-1 py-2 text-sm font-medium transition-colors ${
          activa
            ? 'border-nopal-oscuro text-nopal-oscuro'
            : 'border-transparent text-salvia hover:text-tinta'
        }`}
      >
        {v.etiqueta}
      </button>
    );
  }

  return (
    <header className="sticky top-0 z-20 border-b border-tinta/10 bg-white">
      <div className="mx-auto flex max-w-6xl items-center gap-4 px-5 py-3">
        <span className="font-titulo text-base font-bold tracking-tight text-tinta">
          Comanda
        </span>

        {restaurantes.length > 1 && (
          <select
            value={restauranteId ?? ''}
            onChange={(e) => alCambiarRestaurante(Number(e.target.value))}
            className="rounded-md border-0 bg-transparent py-1 text-sm font-medium text-salvia hover:text-tinta focus:outline-none focus:ring-1 focus:ring-nopal/40"
          >
            {restaurantes.map((r) => (
              <option key={r.id} value={r.id}>
                {r.nombre}
              </option>
            ))}
          </select>
        )}

        <div className="ml-auto flex items-center gap-3">
          <span className="hidden text-sm text-salvia sm:inline">{usuario.nombre}</span>
          <span className="flex h-7 w-7 items-center justify-center rounded-full bg-nopal-claro text-xs font-semibold text-nopal-oscuro">
            {iniciales}
          </span>
          <button
            onClick={() => sesion.cerrar()}
            className="text-sm font-medium text-salvia hover:text-tinta"
          >
            Salir
          </button>
        </div>
      </div>

      <nav className="mx-auto flex max-w-6xl items-center gap-5 px-5">
        <div className="flex flex-1 items-center gap-5 overflow-x-auto">
          {principales.map(pestana)}
        </div>

        <div className="relative" ref={menuRef}>
          <button
            onClick={() => setMenuAbierto((v) => !v)}
            className={`whitespace-nowrap border-b-2 px-1 py-2 text-sm font-medium transition-colors ${
              enSecundaria
                ? 'border-nopal-oscuro text-nopal-oscuro'
                : 'border-transparent text-salvia hover:text-tinta'
            }`}
          >
            Más ⌄
          </button>
          {menuAbierto && (
            <div className="absolute right-0 top-full z-30 mt-1 w-44 rounded-lg border border-tinta/10 bg-white py-1 shadow-lg">
              {secundarias.map((v) => (
                <button
                  key={v.id}
                  onClick={() => {
                    alCambiar(v.id);
                    setMenuAbierto(false);
                  }}
                  className={`block w-full px-3 py-2 text-left text-sm font-medium ${
                    vista === v.id ? 'text-nopal-oscuro' : 'text-salvia hover:text-tinta'
                  }`}
                >
                  {v.etiqueta}
                </button>
              ))}
            </div>
          )}
        </div>
      </nav>
    </header>
  );
}

interface Sucursal {
  id: number;
  nombre: string;
  tipo: 'restaurante' | 'abarrotes';
  meta_minutos: number;
}

function obtenerTokenMesaURL(): string | null {
  const params = new URLSearchParams(window.location.search);
  const q = params.get('mesa');
  if (q) return q;

  const m = window.location.pathname.match(/^\/mesa\/(s\d+-m\d+-v\d+-[a-f0-9]+)$/i);
  if (m) return m[1];

  return null;
}

const CLAVE_RESTAURANTE = 'ericmar_restaurante_id';

function leerRestauranteGuardado(): number | null {
  const valor = localStorage.getItem(CLAVE_RESTAURANTE);
  return valor ? Number(valor) : null;
}

/** El QR fijo de mostrador (para llevar): mismo formato de token, sin mesa. */
function obtenerTokenMostradorURL(): string | null {
  const params = new URLSearchParams(window.location.search);
  const q = params.get('mostrador');
  if (q) return q;

  const m = window.location.pathname.match(/^\/mostrador\/(s\d+-m\d+-v\d+-[a-f0-9]+)$/i);
  if (m) return m[1];

  return null;
}

export default function App() {
  const [tokenMesa] = useState<string | null>(obtenerTokenMesaURL());
  const [tokenMostrador] = useState<string | null>(obtenerTokenMostradorURL());
  const [usuario, setUsuario] = useState<Usuario | null>(sesion.usuario);
  const [sucursales, setSucursales] = useState<Sucursal[]>([]);
  const [vista, setVista] = useState('menu');
  const [restauranteId, setRestauranteId] = useState<number | null>(leerRestauranteGuardado);

  useEffect(() => sesion.suscribir(setUsuario), []);

  useEffect(() => {
    if (!usuario || tokenMesa || tokenMostrador) {
      desconectar();
      setSucursales([]);
      return;
    }
    conectar();
    api<Sucursal[]>('/sucursales')
      .then(setSucursales)
      .catch(() => setSucursales([]));
  }, [usuario, tokenMesa, tokenMostrador]);

  // Si entra por un enlace de QR de mesa o del mostrador, abrir directamente
  // la experiencia del comensal, sin exigirle sesión.
  if (tokenMesa) {
    return <MenuQR token={tokenMesa} />;
  }
  if (tokenMostrador) {
    return <MenuQR token={tokenMostrador} modo="mostrador" />;
  }

  if (!usuario) return <Login />;

  const restaurantes = sucursales.filter((s) => s.tipo === 'restaurante');
  const tienda = sucursales.find((s) => s.tipo === 'abarrotes');
  const restaurante = restaurantes.find((s) => s.id === restauranteId) || restaurantes[0];
  const sucursalId = restaurante?.id || sucursales[0]?.id || 1;

  function cambiarRestaurante(id: number) {
    setRestauranteId(id);
    localStorage.setItem(CLAVE_RESTAURANTE, String(id));
  }

  if (usuario.rol === 'cocina') {
    return (
      <>
        <AvisoConexion />
        <Cocina sucursalId={sucursalId} />
      </>
    );
  }

  if (usuario.rol === 'mesero') {
    return (
      <>
        <AvisoConexion />
        <Mesero sucursalId={sucursalId} />
      </>
    );
  }

  if (usuario.rol === 'cajero') {
    return (
      <>
        <AvisoConexion />
        <Caja sucursalId={sucursalId} />
      </>
    );
  }

  if (usuario.rol === 'repartidor') {
    return (
      <>
        <AvisoConexion />
        <Repartidor sucursalId={sucursalId} />
      </>
    );
  }

  if (usuario.rol === 'dueno') {
    return (
      <>
        <AvisoConexion />
        <BarraAdmin
          usuario={usuario}
          vista={vista}
          alCambiar={setVista}
          restaurantes={restaurantes}
          restauranteId={restaurante?.id ?? null}
          alCambiarRestaurante={cambiarRestaurante}
        />
        {vista === 'caja' ? (
          <Caja sucursalId={sucursalId} />
        ) : vista === 'cocina' ? (
          <Cocina sucursalId={sucursalId} />
        ) : vista === 'mesero' ? (
          <Mesero sucursalId={sucursalId} />
        ) : vista === 'repartidor' ? (
          <Repartidor sucursalId={sucursalId} />
        ) : vista === 'mesas' && restaurante ? (
          <GestionMesas sucursalId={restaurante.id} />
        ) : vista === 'facturacion' ? (
          <Facturacion sucursalId={sucursalId} sucursales={sucursales} />
        ) : vista === 'abarrotes' ? (
          <PosAbarrotes sucursalId={tienda?.id || sucursalId} />
        ) : vista === 'reportes' ? (
          <Reportes sucursalId={sucursalId} />
        ) : vista === 'sucursales' ? (
          <Sucursales sucursales={sucursales} />
        ) : vista === 'personal' ? (
          <Personal sucursales={sucursales} />
        ) : vista === 'dispositivos' ? (
          <Dispositivos sucursales={sucursales} />
        ) : (
          <Catalogo
            key={sucursalId}
            sucursalId={sucursalId}
            tipo="platillo"
            sucursales={sucursales}
          />
        )}
      </>
    );
  }

  return (
    <>
      <AvisoConexion />
      <div className="flex min-h-[70vh] flex-col items-center justify-center gap-3 px-8 text-center">
        <h2 className="text-2xl font-bold">Modo no configurado</h2>
      </div>
      <div className="pb-10 text-center">
        <button className="btn-neutro" onClick={() => sesion.cerrar()}>
          Salir
        </button>
      </div>
    </>
  );
}
